import { Box, Button, Grid, Icon, IconButton, MenuItem, Modal, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import * as yup from "yup";
import { ToastContent } from "../../../shared/components";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../shared/forms";
import { VMaskTextField } from "../../../shared/forms/VMaskTextField";
import { useToast } from "../../../shared/hooks";
import { PeopleService } from "../../../shared/services/api/people/PeopleService";
import { IPeopleCreate } from "../../../shared/services/api/people/types";

interface Props {
	open: boolean;
	onClose: (updateData: boolean) => void;
	onSuccess?: () => void;
	selectedRow?: IPeopleCreate & { id: string }; // ⬅️ Aqui
}

export const ModalCreatePeople = ({ open, onClose, onSuccess, selectedRow }: Props) => {
	const { formRef } = useVForm();
	const [isLoading, setIsLoading] = useState(false);
	const toastHandler = useToast();

	useEffect(() => {
		if (open && selectedRow) {
			setTimeout(() => {
				if (formRef.current) {
					formRef.current.setData(selectedRow);
				}
			}, 0);
		}
	}, [open, selectedRow]);

	const handleSave = async (data: IPeopleCreate) => {
		setIsLoading(true);
		// Garante que document será igual ao cpf
		const dataWithDocument = {
			...data,
			document: data.cpf,
		};

		try {
			const validatedData = await formValidationSchema.validate(dataWithDocument, { abortEarly: false });

			if (selectedRow?.id) {
				await PeopleService.updateById(selectedRow.id, validatedData);
			} else {
				await PeopleService.create(validatedData);
			}

			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Sucesso" content={`Pessoa ${selectedRow ? "atualizada" : "cadastrada"} com sucesso`} />,
			});

			onSuccess?.();
			onClose(true);
		} catch (error) {
			if (error instanceof yup.ValidationError) {
				// console.warn("❌ Erros de validação Yup:", error.errors); // Lista com todas as mensagens
				// console.warn(
				// 	"🔍 Campos com erro:",
				// 	error.inner.map((e) => e.path)
				// ); // Campos com erro

				const validationErrors: IVFormErros = {};
				error.inner.forEach((err) => {
					if (err.path) {
						//console.warn(`Campo inválido: ${err.path} => ${err.message}`);
						validationErrors[err.path] = err.message;
					}
				});

				formRef.current?.setErrors(validationErrors);
			} else {
				console.error("❌ Erro inesperado:", error);
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content={error instanceof Error ? error.message : "Erro inesperado"} />,
				});
			}
		} finally {
			setIsLoading(false);
		}
	};

	const formValidationSchema = yup.object().shape({
		cpf: yup.string().required("CPF obrigatório"),
		document: yup.string().required("Documento obrigatório"),
		status: yup.string().required("Status obrigatório"),
		email: yup.string().email("Email inválido").required("Email obrigatório"),
		name: yup.string().required("Nome obrigatório"),
		birthDate: yup.string().required("Data de nascimento obrigatória"),
		motherName: yup.string().required("Nome da mãe obrigatório"),
		reason: yup.string().nullable().required("Motivo obrigatório"),
	});

	return (
		<Modal open={open} onClose={() => onClose(false)}>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					bgcolor: "background.paper",
					boxShadow: 24,
					p: 4,
					width: "95%",
					maxWidth: 800,
					maxHeight: "90vh",
					overflowY: "auto",
					borderRadius: 2,
				}}
			>
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
					<Typography variant="h5" sx={{ fontWeight: 300 }} color="primary.main">
						{selectedRow ? "Editar BlackList" : "Cadastrar BlackList"}
					</Typography>
					<IconButton onClick={() => onClose(false)}>
						<Icon>close</Icon>
					</IconButton>
				</Box>

				<VForm
					ref={formRef}
					onSubmit={handleSave}
					placeholder=""
					onPointerEnterCapture={() => console.log("entered")}
					onPointerLeaveCapture={() => console.log("left")}
				>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<VTextField label="Nome" name="name" fullWidth disabled={isLoading} />
						</Grid>
						<Grid item xs={12} sm={6}>
							<VMaskTextField label="CPF" name="cpf" fullWidth disabled={isLoading} mask="cpf" />
						</Grid>

						<Grid item xs={12} sm={6}>
							<VTextField label="Email" name="email" fullWidth disabled={isLoading} />
						</Grid>
						<Grid item xs={12} sm={6}>
							<VTextField label="Nascimento" name="birthDate" type="date" fullWidth disabled={isLoading} />
						</Grid>

						<Grid item xs={12} sm={6}>
							<VTextField label="Nome da Mãe" name="motherName" fullWidth disabled={isLoading} />
						</Grid>
						<Grid item xs={12} sm={6}>
							<VTextField label="Status" name="status" fullWidth select disabled={isLoading}>
								<MenuItem value={"active"}>Ativo</MenuItem>
								<MenuItem value={"inactive"}>Inativo</MenuItem>
							</VTextField>
						</Grid>

						<Grid item xs={12}>
							<VTextField label="Motivo" name="reason" required fullWidth disabled={isLoading} multiline minRows={3} />
						</Grid>
					</Grid>

					<Grid container justifyContent="center" spacing={2} mt={3}>
						<Grid item>
							<Button color="inherit" variant="contained" onClick={() => onClose(false)} startIcon={<Icon>arrow_back</Icon>}>
								Voltar
							</Button>
						</Grid>
						<Grid item>
							<Button variant="contained" color="primary" disabled={isLoading} onClick={() => formRef.current?.submitForm()}>
								Confirmar
							</Button>
						</Grid>
					</Grid>
				</VForm>
			</Box>
		</Modal>
	);
};
